//
// selectionSlice — anchor + extent range model (TV1-1690 / B-4b).
//
// Previously stored selection as `Record<string, CellCoordinates>` keyed by
// `${rowId}|${columnId}`. Selecting 5,000 × 50 cells via Ctrl+A produced a
// 250,000-entry map and a 250,000-entry Immer draft on every dispatch.
//
// After B-4b: selection is a small set of rectangular RANGES described by
// indices into the visible-rows / visible-columns arrays. Ctrl+A becomes a
// single range `(0,0)→(N-1, M-1)` — constant payload, constant state size.
//
// Indices are into:
//   - `selectVisualRows` (post-filter/sort/group, collapsed-group bodies hidden)
//   - `columns.filter(c => !c.hidden)`  (visible columns only)
//
// Sort / filter / groupBy / collapsed-groups changes invalidate ranges (a
// range at row index 3 may no longer point to the same row id after sorting).
// We clear ranges on those changes — matches Google Sheets behavior. The
// "snapshot to ids" hook for batch operations is implemented in selectors.ts
// (`selectSelectedCells`, `selectSelectedRowIds`, `selectSelectedColumnIds`)
// which materialize current range positions to row/column IDs at read time.
//
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

export type SelectionRange = {
  anchorRow: number; // index into selectVisualRows
  anchorCol: number; // index into visible columns (columns.filter(c => !c.hidden))
  focusRow: number;
  focusCol: number;
};

export type SelectionState = {
  primary: SelectionRange | null; // shift-extend operates on this
  additional: SelectionRange[]; // ctrl-add separate ranges
  isSelecting: boolean;
};

const initialState: SelectionState = {
  primary: null,
  additional: [],
  isSelecting: false
};

const selectionSlice = createSlice({
  name: 'selection',
  initialState,
  reducers: {
    // Replace primary with a new range, clear additional. This is the path
    // for plain click, click+drag start, shift+click (when caller passes the
    // full extended rectangle), and arrow-nav single-cell selection.
    setPrimaryRange( state, action: PayloadAction<SelectionRange> ) {
      state.primary = action.payload;
      state.additional = [];
    },

    // Shift-drag / shift-extend: update primary.focus, keep anchor. No-op if
    // no primary exists (the caller should `setPrimaryRange` first).
    extendPrimaryFocus( state, action: PayloadAction<{ focusRow: number; focusCol: number }> ) {
      if ( !state.primary ) return;
      state.primary.focusRow = action.payload.focusRow;
      state.primary.focusCol = action.payload.focusCol;
    },

    // Ctrl-add: append a new range to additional[]. Does NOT touch primary.
    addAdditionalRange( state, action: PayloadAction<SelectionRange> ) {
      state.additional.push( action.payload );
    },

    // Select all visible cells with one range. The constant-payload Ctrl+A
    // path — caller passes the dimensions of the current visible grid.
    selectAllVisible( state, action: PayloadAction<{ totalRows: number; totalCols: number }> ) {
      const { totalRows, totalCols } = action.payload;
      if ( totalRows === 0 || totalCols === 0 ) {
        state.primary = null;
        state.additional = [];
        return;
      }
      state.primary = { anchorRow: 0, anchorCol: 0, focusRow: totalRows - 1, focusCol: totalCols - 1 };
      state.additional = [];
    },

    clearSelection( state ) {
      state.primary = null;
      state.additional = [];
    },

    setIsSelecting( state, action: PayloadAction<boolean> ) {
      state.isSelecting = action.payload;
    }
  },
  // Sort / filter / groupBy / collapsed-groups all reshuffle the row index
  // ordering. A range at indices `(2, 0)→(4, 3)` after sort no longer points
  // to the same rows the user selected. Clear ranges on those state changes.
  extraReducers: ( builder ) => {
    builder.addMatcher(
      ( action: { type: string }) =>
        action.type === 'sorting/setSorting' ||
        action.type === 'sorting/clearSorting' ||
        action.type === 'filters/setFilters' ||
        action.type === 'filters/addFilter' ||
        action.type === 'filters/removeFilter' ||
        action.type === 'filters/clearFilters' ||
        action.type === 'grouping/setGroupBy' ||
        action.type === 'grouping/clearGroupBy' ||
        action.type === 'collapsedGroups/toggleCollapsedGroup' ||
        action.type === 'collapsedGroups/setCollapsedGroups',
      ( state ) => {
        state.primary = null;
        state.additional = [];
      }
    );
  }
});

export const {
  setPrimaryRange,
  extendPrimaryFocus,
  addAdditionalRange,
  selectAllVisible,
  clearSelection,
  setIsSelecting
} = selectionSlice.actions;

export const selectSelection = ( state: RootState ) => state.spreadsheet.selection;

export default selectionSlice.reducer;
